import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
    Filter20Regular,
    ArrowSort20Regular,
    Grid20Regular,
    EyeOff20Regular,
    ChevronDown12Regular,
    ChevronDown20Regular,
    MoreVertical16Regular,
    Comment16Filled,
    CommentAdd16Regular,
    AddCircle24Filled,
    ArrowExport20Regular,
    Table20Regular,
    ArrowDownload20Regular,
    Wand16Regular,
    Wand20Regular,
    Dismiss20Regular,
    ArrowClockwise16Regular,
} from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuDivider, OverlayDrawer, DrawerBody } from '@fluentui/react-components';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import styles from '../TaskManagement/css/index.module.css';
import { ToolbarSearchMenu, FilterMultiSelect, filterHasSelection, selectedFilterValues, matchesSelectedValues, applyTextSearch, isFilterSelectMenuTarget } from '../TaskManagement/listToolbarControls';
import { CustomAvatar } from '../TaskManagement';
import ResizableColumnHeader from '../TaskManagement/ResizableColumnHeader';
import {
    GetRaidItems,
    PostRaidItem,
    PatchRaidItem,
    GetProjectRaidOwners,
    PostProjectRaidOwner,
    DeleteRaidItem,
    DownloadRaidTemplate,
    UploadRaidCSV,
    ExportRaidCSV,
    BulkUpdateRaidItems,
    BulkDeleteRaidItems,
    GetRaidAISummary,
    clearRaidSummary,
} from '../../redux/raidSlice';
import { formatDistanceToNow, format, isAfter, subWeeks } from 'date-fns';
import { getGroupBySettings, saveGroupBySettings } from '../../utils/groupByPersistence';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import { toast } from 'react-toastify';
import { handleFlatRowSelection, isEditableSelectionTarget } from '../../utils/tableRowSelectionUtils';
import { exportRaidToPdf } from '../../utils/pdfExports/exportRaidPdf';
import { buildExportPdfFilename } from '../../utils/exportPdfFilename';
import RaidDetailsSidebar from './RaidDetailsSidebar';

const RAID_AI_SUMMARY_SECTIONS = [
    { id: 'overall-risk', label: 'Overall Risk Posture', key: 'overall_risk_posture_html' },
    { id: 'critical-risks', label: 'Critical Risks & Issues', key: 'critical_risks_issues_html' },
    { id: 'immediate-attention', label: 'Items Requiring Immediate Attention', key: 'immediate_attention_items_html' },
    { id: 'key-dependencies', label: 'Key Dependencies', key: 'key_dependencies_html' },
    { id: 'assumptions', label: 'Assumptions to Monitor', key: 'assumptions_to_monitor_html' },
    { id: 'ownership-gaps', label: 'Ownership & Execution Gaps', key: 'ownership_execution_gaps_html' },
    { id: 'mitigation-progress', label: 'Mitigation Progress', key: 'mitigation_progress_html' },
    { id: 'leadership-actions', label: 'Recommended Leadership Actions', key: 'leadership_actions_html' },
];

const RAID_AI_SHOW_ALL = 'show-all';

const Raid = () => {
    const dispatch = useDispatch();
    const {
        raidItems,
        projectRaidOwners,
        uploadCSVBtnLoading,
        exportCSVLoading,
        raidSummaryData,
        raidSummaryLoading,
        raidSummaryError,
    } = useSelector(
        state =>
            state.raid || {
                raidItems: [],
                projectRaidOwners: [],
                uploadCSVBtnLoading: false,
                exportCSVLoading: false,
                raidSummaryData: null,
                raidSummaryLoading: false,
                raidSummaryError: null,
            },
    );
    const { selectedProject } = useSelector(state => state.projectData);
    const { projectUsersAll } = useSelector(state => state.team || { projectUsersAll: [] });

    // Column widths (px)
    const [columnWidths, setColumnWidths] = React.useState({
        id: 60,
        raidItem: 260,
        category: 140,
        impact: 260,
        level: 120,
        probability: 120,
        proximity: 120,
        owner: 80,
        strategy: 260,
        rag: 60,
        loggedOn: 140,
        updatedOn: 200,
        status: 140,
        actions: 40,
    });

    const handleColumnResize = (columnKey, newWidth) => {
        setColumnWidths(prev => ({
            ...prev,
            [columnKey]: newWidth,
        }));
    };

    // Column order (exclude ID and actions like TasksTable excludes checkbox/actions)
    const defaultColumnOrder = ['raidItem', 'category', 'impact', 'level', 'probability', 'proximity', 'owner', 'strategy', 'rag', 'loggedOn', 'updatedOn', 'status'];
    const [columnOrder, setColumnOrder] = React.useState(defaultColumnOrder);

    // Column visibility state (same pattern as TaskManagement)
    const [showAllColumns, setShowAllColumns] = React.useState(true);
    const [hiddenColumns, setHiddenColumns] = React.useState([]);

    // Sort state
    const [sortColumn, setSortColumn] = React.useState('');
    const [sortOrder, setSortOrder] = React.useState('');

    // Group-by state
    const [groupByColumn, setGroupByColumn] = React.useState('');
    const [groupBySort, setGroupBySort] = React.useState('');
    const [collapsedGroups, setCollapsedGroups] = React.useState(new Set());

    // Add-new-RAID row state (visible by default, like TaskManagement)
    const [isAddingNewRaid, setIsAddingNewRaid] = React.useState(true);
    const [newRaidText, setNewRaidText] = React.useState('');
    const [highlightAddRow, setHighlightAddRow] = React.useState(false);
    const newRaidInputRef = React.useRef(null);
    const addRowRef = React.useRef(null);
    const tableContainerRef = React.useRef(null);
    const [addNewRaidFocusTrigger, setAddNewRaidFocusTrigger] = React.useState(0);

    // Inline editing state for raid_item column
    const [editingRaidId, setEditingRaidId] = React.useState(null);
    const [editingRaidText, setEditingRaidText] = React.useState('');
    // Inline editing state for impact column
    const [editingImpactId, setEditingImpactId] = React.useState(null);
    const [editingImpactText, setEditingImpactText] = React.useState('');
    // Inline editing state for strategy column
    const [editingStrategyId, setEditingStrategyId] = React.useState(null);
    const [editingStrategyText, setEditingStrategyText] = React.useState('');

    // Filters state (similar to TaskManagement)
    const [filters, setFilters] = React.useState([{ column: '', value: [], id: Date.now() }]);
    const [filterMenuOpen, setFilterMenuOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const [newOwnerName, setNewOwnerName] = React.useState('');
    const [ownerMenuOpenRowId, setOwnerMenuOpenRowId] = React.useState(null);
    const [selectedRowForDelete, setSelectedRowForDelete] = React.useState(null);
    const deleteModalRef = React.useRef(null);
    const [selectedRows, setSelectedRows] = React.useState([]);
    const lastSelectedRowIdRef = React.useRef(null);
    const [exportPDFLoading, setExportPDFLoading] = React.useState(false);
    const [hoveredRowId, setHoveredRowId] = React.useState(null);
    const [headerIdHovered, setHeaderIdHovered] = React.useState(false);

    // CSV actions
    const handleExportRaidCSV = () => {
        if (!exportCSVLoading) {
            dispatch(ExportRaidCSV());
        }
    };

    const handleExportSelectedRaidCSV = () => {
        if (exportCSVLoading || selectedRows.length === 0) {
            return;
        }
        dispatch(ExportRaidCSV(selectedRows));
    };

    const handleImportRaidCSV = () => {
        const input = document.getElementById('csv-upload-raid');
        if (input && !uploadCSVBtnLoading) {
            input.click();
        }
    };

    const handleDownloadRaidTemplate = () => {
        dispatch(DownloadRaidTemplate());
    };

    const handleRaidFileUpload = event => {
        const file = event.target.files[0];
        if (file && !uploadCSVBtnLoading) {
            const formData = new FormData();
            formData.append('file', file);
            dispatch(UploadRaidCSV(formData));
        }
        // reset input so same file can be chosen again if needed
        event.target.value = '';
    };

    const [raidSidebarOpen, setRaidSidebarOpen] = React.useState(false);
    const [selectedRaidForSidebar, setSelectedRaidForSidebar] = React.useState(null);
    const [showLensAIAnalysis, setShowLensAIAnalysis] = React.useState(false);
    const [selectedSummaryTab, setSelectedSummaryTab] = React.useState('overall-risk');

    const handleOpenRaidSidebar = row => {
        setSelectedRaidForSidebar(row);
        setRaidSidebarOpen(true);
    };

    const handleCloseRaidSidebar = () => {
        setRaidSidebarOpen(false);
        setSelectedRaidForSidebar(null);
    };

    const isRowSelected = id => selectedRows.includes(id);

    React.useEffect(() => {
        if (selectedRows.length === 0) {
            lastSelectedRowIdRef.current = null;
        }
    }, [selectedRows]);

    const getVisibleRowIds = () => {
        if (!groupByColumn) {
            return flatVisibleItems.map(item => item.id);
        }

        const grouped = applyGrouping(flatVisibleItems);
        if (!grouped) return [];

        return grouped.filter(group => !collapsedGroups.has(group.groupName)).flatMap(group => group.items.map(item => item.id));
    };

    const handleSelectRow = (id, event) => {
        handleFlatRowSelection({
            rowId: id,
            event,
            getVisibleIds: getVisibleRowIds,
            lastSelectedIdRef: lastSelectedRowIdRef,
            setSelectedRows,
        });
    };

    const handleRowPointerDown = (rowId, event) => {
        if (event.shiftKey && event.button === 0 && !isEditableSelectionTarget(event.target)) {
            event.preventDefault();
            handleSelectRow(rowId, event);
        }
    };

    const totalTableWidth = React.useMemo(() => Object.values(columnWidths).reduce((sum, width) => sum + (Number(width) || 0), 0), [columnWidths]);

    React.useEffect(() => {
        if (selectedProject?.id) {
            dispatch(GetRaidItems());
            dispatch(GetProjectRaidOwners());
        }
    }, [dispatch, selectedProject?.id]);

    const [searchParams, setSearchParams] = useSearchParams();

    React.useEffect(() => {
        const raidId = searchParams.get('raidId');
        if (!raidId || !raidItems?.length) return;

        const row = raidItems.find(item => String(item.id) === String(raidId));
        if (!row) return;

        handleOpenRaidSidebar(row);

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('raidId');
        setSearchParams(nextParams, { replace: true });
    }, [raidItems, searchParams, setSearchParams]);

    // Auto-load latest RAID AI summary when Lens AI drawer opens (similar to Lessons/IRL)
    React.useEffect(() => {
        if (!showLensAIAnalysis) return;
        if (!selectedRows || selectedRows.length === 0) return;
        if (raidSummaryLoading || raidSummaryData || raidSummaryError) return;
        dispatch(GetRaidAISummary({ raidItemIds: selectedRows, regenerate: false }));
    }, [showLensAIAnalysis, selectedRows, raidSummaryLoading, raidSummaryData, raidSummaryError, dispatch]);

    const isColumnVisible = column => {
        if (showAllColumns) return true;
        return !hiddenColumns.includes(column);
    };

    const handleShowAllColumns = checked => {
        if (checked) {
            setShowAllColumns(true);
            setHiddenColumns([]);
        } else {
            setShowAllColumns(false);
        }
    };

    const handleColumnVisibility = (column, checked) => {
        if (column === 'showAll') {
            handleShowAllColumns(checked);
            return;
        }

        if (checked) {
            // Show column
            setHiddenColumns(prev => prev.filter(col => col !== column));
            if (hiddenColumns.length === 0) {
                setShowAllColumns(true);
            }
        } else {
            // Hide column
            setHiddenColumns(prev => [...prev, column]);
            setShowAllColumns(false);
        }
    };

    const getColumnVisibilityOptions = () => [
        { value: 'showAll', label: 'Show all' },
        { value: 'raidItem', label: 'RAID Item' },
        { value: 'category', label: 'RAID Category' },
        { value: 'impact', label: 'Impact' },
        { value: 'level', label: 'Level' },
        { value: 'probability', label: 'Probability' },
        { value: 'proximity', label: 'Proximity' },
        { value: 'owner', label: 'Owner' },
        { value: 'strategy', label: 'Strategy / Mitigation' },
        { value: 'rag', label: 'RAG' },
        { value: 'loggedOn', label: 'Logged on' },
        { value: 'updatedOn', label: 'Updated on' },
        { value: 'status', label: 'Current Status' },
    ];

    // Sortable header cell (copied pattern from TasksTable)
    const SortableHeaderCell = ({ id, width, children }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
        const style = {
            transform: transform ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)` : undefined,
            transition,
            opacity: isDragging ? 0.6 : 1,
            width: `${width}px`,
            minWidth: `${width}px`,
            maxWidth: `${width}px`,
            '--column-width': `${width}px`,
            cursor: isDragging ? 's-resize' : undefined,
        };
        return (
            <th ref={setNodeRef} style={style} className={`${styles.headerRowItem} ${isDragging ? 'isDragging' : ''}`} data-sortable-id={id} {...attributes} {...listeners}>
                <div style={{ width: '100%' }}>{children}</div>
            </th>
        );
    };

    // Load RAID items and RAID owners when project changes
    React.useEffect(() => {
        if (selectedProject?.id) {
            dispatch(GetRaidItems());
            dispatch(GetProjectRaidOwners());
        }
    }, [dispatch, selectedProject?.id]);

    // Focus and scroll to add-new-RAID row when activated or when trigger changes
    React.useEffect(() => {
        if (isAddingNewRaid) {
            requestAnimationFrame(() => {
                if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = tableContainerRef.current.scrollHeight;
                }
                if (addRowRef.current) {
                    addRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
                if (newRaidInputRef.current) {
                    newRaidInputRef.current.focus();
                }
            });

            setHighlightAddRow(true);
            const timeoutId = setTimeout(() => {
                setHighlightAddRow(false);
            }, 1000);

            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, [isAddingNewRaid, addNewRaidFocusTrigger]);

    // Load persisted group-by settings when project changes
    React.useEffect(() => {
        if (selectedProject?.id) {
            const saved = getGroupBySettings(selectedProject.id + '_raid');
            if (saved) {
                setGroupByColumn(saved.column || '');
                setGroupBySort(saved.sort || '');
                setCollapsedGroups(new Set(saved.collapsedGroups || []));
            } else {
                setGroupByColumn('');
                setGroupBySort('');
                setCollapsedGroups(new Set());
            }
        }
    }, [selectedProject?.id]);

    // Persist group-by settings when they change
    React.useEffect(() => {
        if (!selectedProject?.id) return;
        saveGroupBySettings(selectedProject.id + '_raid', {
            column: groupByColumn,
            sort: groupBySort,
            collapsedGroups: Array.from(collapsedGroups),
        });
    }, [selectedProject?.id, groupByColumn, groupBySort, collapsedGroups]);

    const handleAddNewRaid = () => {
        setIsAddingNewRaid(true);
        setNewRaidText('');
        // Bump focus trigger so effect runs even if row is already visible
        setAddNewRaidFocusTrigger(prev => prev + 1);
    };

    const handleRaidTextClick = (e, row) => {
        e.stopPropagation();
        setEditingRaidId(row.id);
        setEditingRaidText(row.raid_item || '');
    };

    const handleRaidTextSave = async row => {
        const original = row.raid_item || '';
        if (editingRaidText.trim() !== original.trim()) {
            try {
                await dispatch(
                    PatchRaidItem({
                        id: row.id,
                        body: { raid_item: editingRaidText },
                    }),
                );
            } catch (error) {
                // Swallow error here, toast is handled in slice
            }
        }
        setEditingRaidId(null);
        setEditingRaidText('');
    };

    const handleRaidTextCancel = () => {
        setEditingRaidId(null);
        setEditingRaidText('');
    };

    const handleRaidTextKeyPress = (e, row) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
            handleRaidTextSave(row);
        } else if (e.key === 'Escape') {
            handleRaidTextCancel();
        }
    };

    const handleImpactClick = (e, row) => {
        e.stopPropagation();
        setEditingImpactId(row.id);
        setEditingImpactText(row.impact || '');
    };

    const handleImpactSave = async row => {
        const original = row.impact || '';
        if (editingImpactText.trim() !== original.trim()) {
            try {
                await dispatch(
                    PatchRaidItem({
                        id: row.id,
                        body: { impact: editingImpactText },
                    }),
                );
            } catch (error) {
                // Error toasts handled in slice
            }
        }
        setEditingImpactId(null);
        setEditingImpactText('');
    };

    const handleImpactCancel = () => {
        setEditingImpactId(null);
        setEditingImpactText('');
    };

    const handleImpactKeyPress = (e, row) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
            handleImpactSave(row);
        } else if (e.key === 'Escape') {
            handleImpactCancel();
        }
    };

    const handleStrategyClick = (e, row) => {
        e.stopPropagation();
        setEditingStrategyId(row.id);
        setEditingStrategyText(row.strategy || '');
    };

    const handleStrategySave = async row => {
        const original = row.strategy || '';
        if (editingStrategyText.trim() !== original.trim()) {
            try {
                await dispatch(
                    PatchRaidItem({
                        id: row.id,
                        body: { strategy: editingStrategyText },
                    }),
                );
            } catch (error) {
                // Error toasts handled in slice
            }
        }
        setEditingStrategyId(null);
        setEditingStrategyText('');
    };

    const handleStrategyCancel = () => {
        setEditingStrategyId(null);
        setEditingStrategyText('');
    };

    const handleStrategyKeyPress = (e, row) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
            handleStrategySave(row);
        } else if (e.key === 'Escape') {
            handleStrategyCancel();
        }
    };

    const handleCategoryChange = async (row, value) => {
        try {
            await dispatch(
                PatchRaidItem({
                    id: row.id,
                    body: { raid_category: value },
                }),
            );
        } catch (error) {
            // Errors are toasted in the slice
        }
    };

    const handleLevelChange = async (row, value) => {
        try {
            await dispatch(
                PatchRaidItem({
                    id: row.id,
                    body: { level: value },
                }),
            );
        } catch (error) {
            // Errors are toasted in the slice
        }
    };

    const handleProbabilityChange = async (row, value) => {
        try {
            await dispatch(
                PatchRaidItem({
                    id: row.id,
                    body: { probability: value },
                }),
            );
        } catch (error) {
            // Errors are toasted in the slice
        }
    };

    const handleProximityChange = async (row, value) => {
        try {
            await dispatch(
                PatchRaidItem({
                    id: row.id,
                    body: { proximity: value },
                }),
            );
        } catch (error) {
            // Errors are toasted in the slice
        }
    };

    const handleStatusChange = async (row, value) => {
        try {
            await dispatch(
                PatchRaidItem({
                    id: row.id,
                    body: { status: value },
                }),
            );
        } catch (error) {
            // Errors are toasted in the slice
        }
    };

    const handleRagChange = async (row, value) => {
        try {
            await dispatch(
                PatchRaidItem({
                    id: row.id,
                    body: { rag: value },
                }),
            );
        } catch (error) {
            // Errors are toasted in the slice
        }
    };

    // Filter helpers
    const handleAddFilter = () => {
        setFilters(prev => [...prev, { column: '', value: [], id: Date.now() }]);
    };

    const handleRemoveFilter = filterId => {
        setFilters(prev => prev.filter(filter => filter.id !== filterId));
    };

    const handleUpdateFilter = (filterId, field, value) => {
        setFilters(prev =>
            prev.map(filter => {
                if (filter.id !== filterId) return filter;
                if (field === 'column') return { ...filter, column: value, value: [] };
                return { ...filter, [field]: value };
            }),
        );
    };

    const handleClearAllFilters = () => {
        setFilters([{ column: '', value: [], id: Date.now() }]);
    };

    const handleClearSort = () => {
        setSortColumn('');
        setSortOrder('');
    };

    const handleCreateRaid = async () => {
        const value = newRaidText.trim();
        if (!value) return;
        await dispatch(
            PostRaidItem({
                raid_item: value,
            }),
        );
        setNewRaidText('');
    };

    const formatDate = dateString => {
        if (!dateString) return '-- --';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        } catch (e) {
            return '-- --';
        }
    };

    const getCategoryInfo = status => {
        const statusMap = {
            // Backend codes
            RISK: { bg: '#C0392B', text: 'Risk' },
            ASSUMPTION: { bg: '#7D3C98', text: 'Assumption' },
            ISSUE: { bg: '#2874A6', text: 'Issue' },
            DEPENDENCY: { bg: '#1E8449', text: 'Dependency' },
            // Fallback support for any legacy/display values
            Risk: { bg: '#C0392B', text: 'Risk' },
            Assumption: { bg: '#7D3C98', text: 'Assumption' },
            Issue: { bg: '#2874A6', text: 'Issue' },
            Dependency: { bg: '#1E8449', text: 'Dependency' },
        };

        if (status && statusMap[status]) {
            return statusMap[status];
        }

        // Default for empty/unspecified category
        return { bg: '#C0C6D8', text: 'Unspecified' };
    };

    const getLevelInfo = level => {
        const levelMap = {
            // Backend codes
            NEGLIGIBLE: { bg: '#7F8C8D', text: 'Negligible' },
            LOW: { bg: '#27AE60', text: 'Low' },
            MODERATE: { bg: '#F1C40F', text: 'Moderate' },
            HIGH: { bg: '#E67E22', text: 'High' },
            CRITICAL: { bg: '#C0392B', text: 'Critical' },
            // Fallback support for any legacy/display values
            Negligible: { bg: '#7F8C8D', text: 'Negligible' },
            Low: { bg: '#27AE60', text: 'Low' },
            Moderate: { bg: '#F1C40F', text: 'Moderate' },
            High: { bg: '#E67E22', text: 'High' },
            Critical: { bg: '#C0392B', text: 'Critical' },
        };

        if (level && levelMap[level]) {
            return levelMap[level];
        }

        // Default for empty/unspecified level
        return { bg: '#C0C6D8', text: 'Unspecified' };
    };

    const getProbabilityInfo = probability => {
        const probabilityMap = {
            // Backend codes
            HIGH: { bg: '#2B35AF', text: 'High' },
            MEDIUM: { bg: '#4361EE', text: 'Medium' },
            LOW: { bg: '#4895EF', text: 'Low' },
            // Fallback display variants
            High: { bg: '#2B35AF', text: 'High' },
            Medium: { bg: '#4361EE', text: 'Medium' },
            Low: { bg: '#4895EF', text: 'Low' },
        };

        if (probability && probabilityMap[probability]) {
            return probabilityMap[probability];
        }

        // Default for empty/unspecified probability
        return { bg: '#C0C6D8', text: 'Unspecified' };
    };

    const getProximityInfo = proximity => {
        const proximityMap = {
            // Backend codes
            '0-1': { bg: '#922B21', text: '0 - 1 mo' },
            '1-3': { bg: '#AF601A', text: '1 - 3 mo' },
            '3-12': { bg: '#B7950B', text: '3 - 12 mo' },
            '12+': { bg: '#5D6D7E', text: '12+ mo' },
            // Fallback display variants
            '0 - 1': { bg: '#922B21', text: '0 - 1 mo' },
            '1 - 3': { bg: '#AF601A', text: '1 - 3 mo' },
            '3 - 12': { bg: '#B7950B', text: '3 - 12 mo' },
        };

        if (proximity && proximityMap[proximity]) {
            return proximityMap[proximity];
        }

        // Default for empty/unspecified proximity
        return { bg: '#C0C6D8', text: 'Unspecified' };
    };

    const formatLastUpdated = timestamp => {
        if (!timestamp) return '--';
        const date = new Date(timestamp);
        const oneWeekAgo = subWeeks(new Date(), 1);

        if (isAfter(date, oneWeekAgo)) {
            return formatDistanceToNow(date, { addSuffix: true });
        } else {
            return format(date, 'MMM d, yyyy');
        }
    };
    const getStatusInfo = status => {
        const statusMap = {
            // Backend status values
            NOT_STARTED: { bg: '#C0C6D8', text: 'Not Started' },
            WORKING_ON_IT: { bg: 'var(--RAG-Amber)', text: 'Working on it' },
            COMPLETED: { bg: 'var(--RAG-Green)', text: 'Completed' },
            STUCK: { bg: 'var(--RAG-Red)', text: 'Stuck' },
            CANCELLED: { bg: '#989BAD', text: 'Cancelled' },
            // Fallback display variants
            'Not Started': { bg: '#C0C6D8', text: 'Not Started' },
            'Working on it': { bg: 'var(--RAG-Amber)', text: 'Working on it' },
            Completed: { bg: 'var(--RAG-Green)', text: 'Completed' },
            Stuck: { bg: 'var(--RAG-Red)', text: 'Stuck' },
            Cancelled: { bg: '#989BAD', text: 'Cancelled' },
        };

        if (status && statusMap[status]) {
            return statusMap[status];
        }

        // Default for empty/unspecified status
        return { bg: '#C0C6D8', text: 'Unspecified' };
    };

    const getRagInfo = rag => {
        const ragMap = {
            // Backend codes
            RED: { bg: 'var(--RAG-Red)', text: 'Red' },
            AMBER: { bg: 'var(--RAG-Amber)', text: 'Amber' },
            GREEN: { bg: 'var(--RAG-Green)', text: 'Green' },
            // Display variants
            Red: { bg: 'var(--RAG-Red)', text: 'Red' },
            Amber: { bg: 'var(--RAG-Amber)', text: 'Amber' },
            Green: { bg: 'var(--RAG-Green)', text: 'Green' },
        };

        if (ragMap[rag]) {
            return ragMap[rag];
        }

        return { bg: '#C0C6D8', text: 'Not Applicable' };
    };

    const allOwners = React.useMemo(() => {
        const ownerMap = new Map();

        // Backend project RAID owners
        if (projectRaidOwners && projectRaidOwners.length > 0) {
            projectRaidOwners.forEach(owner => {
                if (owner.name) {
                    ownerMap.set(owner.name, owner.name);
                }
            });
        }

        // Project team members
        if (projectUsersAll && projectUsersAll.length > 0) {
            projectUsersAll.forEach(user => {
                const fullName = user?.value?.full_name;
                if (fullName && !ownerMap.has(fullName)) {
                    ownerMap.set(fullName, fullName);
                }
            });
        }

        // Owners from RAID items
        if (raidItems && raidItems.length > 0) {
            raidItems.forEach(item => {
                if (item.owner && !ownerMap.has(item.owner)) {
                    ownerMap.set(item.owner, item.owner);
                } else if (item.created_by_name && !ownerMap.has(item.created_by_name)) {
                    ownerMap.set(item.created_by_name, item.created_by_name);
                }
            });
        }

        return Array.from(ownerMap.values());
    }, [projectRaidOwners, projectUsersAll, raidItems]);

    const getFilterColumnOptions = () => [
        { value: 'raid_category', label: 'RAID Category' },
        { value: 'level', label: 'Level' },
        { value: 'probability', label: 'Probability' },
        { value: 'proximity', label: 'Proximity' },
        { value: 'owner', label: 'Owner' },
        { value: 'rag', label: 'RAG' },
        { value: 'status', label: 'Status' },
    ];

    const getSortColumnOptions = () => [
        { value: 'raid_category', label: 'RAID Category' },
        { value: 'level', label: 'Level' },
        { value: 'probability', label: 'Probability' },
        { value: 'proximity', label: 'Proximity' },
        { value: 'rag', label: 'RAG' },
        { value: 'loggedOn', label: 'Logged on' },
        { value: 'updatedOn', label: 'Updated on' },
        { value: 'status', label: 'Status' },
    ];

    const getSortOrderOptions = () => [
        { value: 'asc', label: 'Ascending' },
        { value: 'desc', label: 'Descending' },
    ];

    const getFilterValueOptions = column => {
        const items = raidItems || [];
        if (!column) return [];

        const addUnique = (acc, value, label = null) => {
            if (!value) return acc;
            if (!acc.find(opt => opt.value === value)) {
                acc.push({ value, label: label || value });
            }
            return acc;
        };

        switch (column) {
            case 'raid_category':
                return items.reduce((acc, item) => addUnique(acc, item.raid_category, getCategoryInfo(item.raid_category).text), []);
            case 'level':
                return items.reduce((acc, item) => addUnique(acc, item.level, getLevelInfo(item.level).text), []);
            case 'probability':
                return items.reduce((acc, item) => addUnique(acc, item.probability, getProbabilityInfo(item.probability).text), []);
            case 'proximity':
                return items.reduce((acc, item) => addUnique(acc, item.proximity, getProximityInfo(item.proximity).text), []);
            case 'owner':
                return items.reduce((acc, item) => addUnique(acc, item.owner || item.created_by_name, item.owner || item.created_by_name), []);
            case 'rag':
                return items.reduce((acc, item) => addUnique(acc, item.rag, getRagInfo(item.rag).text), []);
            case 'status':
                return items.reduce((acc, item) => addUnique(acc, item.status, getStatusInfo(item.status).text), []);
            default:
                return [];
        }
    };

    const applyFilters = items => {
        if (!items || !filters?.length) return items;

        return items.filter(item =>
            filters.every(filter => {
                const selectedValues = selectedFilterValues(filter.value);
                if (!filter.column || selectedValues.length === 0) return true;

                switch (filter.column) {
                    case 'raid_category':
                        return matchesSelectedValues(item.raid_category, selectedValues);
                    case 'level':
                        return matchesSelectedValues(item.level, selectedValues);
                    case 'probability':
                        return matchesSelectedValues(item.probability, selectedValues);
                    case 'proximity':
                        return matchesSelectedValues(item.proximity, selectedValues);
                    case 'owner': {
                        const ownerName = item.owner || item.created_by_name || '';
                        return matchesSelectedValues(ownerName, selectedValues);
                    }
                    case 'rag':
                        return matchesSelectedValues(item.rag, selectedValues);
                    case 'status':
                        return matchesSelectedValues(item.status, selectedValues);
                    default:
                        return true;
                }
            }),
        );
    };

    const applySorting = items => {
        if (!items || !sortColumn || !sortOrder) return items;

        if (sortColumn === 'loggedOn' || sortColumn === 'updatedOn') {
            const dateKey = sortColumn === 'loggedOn' ? 'created' : 'last_updated';
            return [...items].sort((a, b) => {
                const aTime = a?.[dateKey] ? new Date(a[dateKey]).getTime() : 0;
                const bTime = b?.[dateKey] ? new Date(b[dateKey]).getTime() : 0;
                return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
            });
        }

        const orderMaps = {
            raid_category: ['RISK', 'ASSUMPTION', 'ISSUE', 'DEPENDENCY'],
            level: ['CRITICAL', 'HIGH', 'MODERATE', 'LOW', 'NEGLIGIBLE'],
            probability: ['HIGH', 'MEDIUM', 'LOW'],
            proximity: ['0-1', '1-3', '3-12', '12+'],
            rag: ['RED', 'AMBER', 'GREEN'],
            status: ['NOT_STARTED', 'WORKING_ON_IT', 'COMPLETED', 'STUCK', 'CANCELLED'],
        };

        const orderArray = orderMaps[sortColumn];
        if (!orderArray) return items;

        const getValueForColumn = item => {
            switch (sortColumn) {
                case 'raid_category':
                    return item.raid_category;
                case 'level':
                    return item.level;
                case 'probability':
                    return item.probability;
                case 'proximity':
                    return item.proximity;
                case 'rag':
                    return item.rag;
                case 'status':
                    return item.status;
                default:
                    return null;
            }
        };

        const sorted = [...items].sort((a, b) => {
            const aVal = getValueForColumn(a);
            const bVal = getValueForColumn(b);

            const aIndex = orderArray.indexOf(aVal);
            const bIndex = orderArray.indexOf(bVal);

            const aRank = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
            const bRank = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;

            if (aRank === bRank) return 0;
            if (sortOrder === 'asc') {
                return aRank - bRank;
            } else {
                return bRank - aRank;
            }
        });

        return sorted;
    };

    const getRaidSearchText = item =>
        [
            item.raid_item,
            item.impact,
            item.strategy,
            item.owner,
            item.created_by_name,
            getCategoryInfo(item.raid_category).text,
            getLevelInfo(item.level).text,
            getProbabilityInfo(item.probability).text,
            getProximityInfo(item.proximity).text,
            getRagInfo(item.rag).text,
            getStatusInfo(item.status).text,
        ]
            .filter(Boolean)
            .join(' ');

    const flatVisibleItems = React.useMemo(
        () => applySorting(applyTextSearch(applyFilters(raidItems && raidItems.length ? raidItems : []), searchQuery, getRaidSearchText)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [raidItems, filters, sortColumn, sortOrder, searchQuery],
    );
    const visibleIds = React.useMemo(() => flatVisibleItems.map(item => item.id), [flatVisibleItems]);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedRows.includes(id));

    const handleToggleSelectAll = () => {
        if (allVisibleSelected) {
            // Unselect all visible
            setSelectedRows(prev => prev.filter(id => !visibleIds.includes(id)));
        } else {
            // Select all visible (merge with any existing selections)
            setSelectedRows(prev => {
                const merged = new Set([...prev, ...visibleIds]);
                return Array.from(merged);
            });
        }
    };

    const handleDeleteRow = row => {
        setSelectedRowForDelete(row);
        deleteModalRef?.current?.click();
    };

    const handleOwnerSelect = async (row, ownerName) => {
        try {
            await dispatch(
                PatchRaidItem({
                    id: row.id,
                    body: { owner: ownerName },
                }),
            );
        } catch (error) {
            // Errors are toasted in slice
        } finally {
            setOwnerMenuOpenRowId(null);
        }
    };

    const handleAddOwner = async () => {
        const value = newOwnerName.trim();
        if (!value) return;
        try {
            await dispatch(
                PostProjectRaidOwner({
                    name: value,
                }),
            );
        } catch (error) {
            // Errors are toasted in slice
        } finally {
            setNewOwnerName('');
        }
    };

    const applyGrouping = items => {
        if (!items || !groupByColumn) return null;

        const groups = {};

        items.forEach(item => {
            let key = '';
            switch (groupByColumn) {
                case 'raid_category':
                    key = getCategoryInfo(item.raid_category).text || 'Not Applicable';
                    break;
                case 'level':
                    key = getLevelInfo(item.level).text || 'Not Applicable';
                    break;
                case 'probability':
                    key = getProbabilityInfo(item.probability).text || 'Not Applicable';
                    break;
                case 'proximity':
                    key = getProximityInfo(item.proximity).text || 'Not Applicable';
                    break;
                case 'owner':
                    key = item.owner || item.created_by_name || 'Not Applicable';
                    break;
                case 'rag':
                    key = getRagInfo(item.rag).text || 'Not Applicable';
                    break;
                case 'status':
                    key = getStatusInfo(item.status).text || 'Not Applicable';
                    break;
                default:
                    key = 'Other';
            }
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(item);
        });

        let entries = Object.entries(groups).map(([groupName, groupItems]) => ({
            groupName,
            items: groupItems,
        }));

        if (groupBySort === 'asc') {
            entries.sort((a, b) => a.groupName.localeCompare(b.groupName));
        } else if (groupBySort === 'desc') {
            entries.sort((a, b) => b.groupName.localeCompare(a.groupName));
        }

        return entries;
    };

    const handleExportRaidPDF = async (selectedOnly = false) => {
        if (exportPDFLoading) return;
        setExportPDFLoading(true);
        try {
            const grouped = groupByColumn ? applyGrouping(flatVisibleItems) : null;
            await exportRaidToPdf({
                rows: flatVisibleItems,
                groupedSections: grouped,
                columnOrder,
                isColumnVisible,
                projectName: selectedProject?.name,
                selectedRowIds: selectedOnly ? selectedRows : null,
                filename: buildExportPdfFilename({
                    project: selectedProject,
                    label: 'RAID',
                    detail: selectedOnly ? `selected ${selectedRows.length}` : '',
                }),
            });
            toast.success(selectedOnly ? `Exported ${selectedRows.length} RAID item(s) to PDF` : 'RAID exported to PDF');
        } catch (error) {
            toast.error(error?.message || 'Failed to export RAID to PDF');
        } finally {
            setExportPDFLoading(false);
        }
    };

    // Helper to render a body cell for a given row + column key
    const renderBodyCell = (row, col) => {
        switch (col) {
            case 'raidItem':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.raidItem }}>
                        {editingRaidId === row.id ? (
                            <input
                                type="text"
                                className={'form-control form-control-sm ' + styles.taskTextField}
                                value={editingRaidText}
                                onChange={e => {
                                    e.stopPropagation();
                                    setEditingRaidText(e.target.value);
                                }}
                                onKeyDown={e => handleRaidTextKeyPress(e, row)}
                                onBlur={e => {
                                    e.stopPropagation();
                                    handleRaidTextSave(row);
                                }}
                                autoFocus
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <div className="d-flex align-items-center">
                                <div className="flex-fill" style={{ minWidth: 0 }}>
                                    <div
                                        className={styles.taskText}
                                        title={row.raid_item || '-- --'}
                                        onClick={e => handleRaidTextClick(e, row)}
                                        style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            width: '100%',
                                        }}>
                                        {row.raid_item || '-- --'}
                                    </div>
                                </div>
                                <div className="d-flex">
                                    <div className={styles.commentIcon} onClick={() => handleOpenRaidSidebar(row)} style={{ cursor: 'pointer' }}>
                                        {row?.comments_count > 0 ? <Comment16Filled color="var(--Brand-Secondary)" /> : <CommentAdd16Regular />}
                                    </div>
                                </div>
                            </div>
                        )}
                    </td>
                );
            case 'category':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.category }}>
                        <Menu>
                            <MenuTrigger>
                                <div
                                    className={styles.statusDiv}
                                    style={{
                                        backgroundColor: getCategoryInfo(row?.raid_category)?.bg,
                                        color: 'white',
                                        cursor: 'pointer',
                                    }}>
                                    {getCategoryInfo(row?.raid_category)?.text || row?.raid_category}
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: '118px', minWidth: '118px' }}>
                                <MenuList>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleCategoryChange(row, 'RISK')}
                                        style={{ backgroundColor: getCategoryInfo('RISK').bg, color: 'white' }}>
                                        Risk
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleCategoryChange(row, 'ASSUMPTION')}
                                        style={{ backgroundColor: getCategoryInfo('ASSUMPTION').bg, color: 'white' }}>
                                        Assumption
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleCategoryChange(row, 'ISSUE')}
                                        style={{ backgroundColor: getCategoryInfo('ISSUE').bg, color: 'white' }}>
                                        Issue
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleCategoryChange(row, 'DEPENDENCY')}
                                        style={{ backgroundColor: getCategoryInfo('DEPENDENCY').bg, color: 'white' }}>
                                        Dependency
                                    </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </td>
                );
            case 'impact':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.impact }}>
                        {editingImpactId === row.id ? (
                            <input
                                type="text"
                                className={'form-control form-control-sm ' + styles.taskTextField}
                                value={editingImpactText}
                                onChange={e => {
                                    e.stopPropagation();
                                    setEditingImpactText(e.target.value);
                                }}
                                onKeyDown={e => handleImpactKeyPress(e, row)}
                                onBlur={e => {
                                    e.stopPropagation();
                                    handleImpactSave(row);
                                }}
                                autoFocus
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <div
                                className={styles.taskText}
                                title={row.impact || 'Pending Input'}
                                onClick={e => handleImpactClick(e, row)}
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: '100%',
                                    color: row.impact ? 'var(--Brand-Primary)' : 'var(--Neutral-Gray-6)',
                                }}>
                                {row.impact || 'Pending Input'}
                            </div>
                        )}
                    </td>
                );
            case 'level':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.level }}>
                        <Menu>
                            <MenuTrigger>
                                <div
                                    className={styles.statusDiv}
                                    style={{
                                        backgroundColor: getLevelInfo(row?.level).bg,
                                        color: 'white',
                                        cursor: 'pointer',
                                    }}>
                                    {getLevelInfo(row?.level).text}
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: '140px', minWidth: '140px' }}>
                                <MenuList>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleLevelChange(row, 'NEGLIGIBLE')}
                                        style={{ backgroundColor: getLevelInfo('NEGLIGIBLE').bg, color: 'white' }}>
                                        Negligible
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleLevelChange(row, 'LOW')}
                                        style={{ backgroundColor: getLevelInfo('LOW').bg, color: 'white' }}>
                                        Low
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleLevelChange(row, 'MODERATE')}
                                        style={{ backgroundColor: getLevelInfo('MODERATE').bg, color: 'white' }}>
                                        Moderate
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleLevelChange(row, 'HIGH')}
                                        style={{ backgroundColor: getLevelInfo('HIGH').bg, color: 'white' }}>
                                        High
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleLevelChange(row, 'CRITICAL')}
                                        style={{ backgroundColor: getLevelInfo('CRITICAL').bg, color: 'white' }}>
                                        Critical
                                    </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </td>
                );
            case 'probability':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.probability }}>
                        <Menu>
                            <MenuTrigger>
                                <div
                                    className={styles.statusDiv}
                                    style={{
                                        backgroundColor: getProbabilityInfo(row?.probability).bg,
                                        color: 'white',
                                        cursor: 'pointer',
                                    }}>
                                    {getProbabilityInfo(row?.probability).text}
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: '140px', minWidth: '140px' }}>
                                <MenuList>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleProbabilityChange(row, 'HIGH')}
                                        style={{ backgroundColor: getProbabilityInfo('HIGH').bg, color: 'white' }}>
                                        High
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleProbabilityChange(row, 'MEDIUM')}
                                        style={{ backgroundColor: getProbabilityInfo('MEDIUM').bg, color: 'white' }}>
                                        Medium
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleProbabilityChange(row, 'LOW')}
                                        style={{ backgroundColor: getProbabilityInfo('LOW').bg, color: 'white' }}>
                                        Low
                                    </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </td>
                );
            case 'proximity':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.proximity }}>
                        <Menu>
                            <MenuTrigger>
                                <div
                                    className={styles.statusDiv}
                                    style={{
                                        backgroundColor: getProximityInfo(row?.proximity).bg,
                                        color: 'white',
                                        cursor: 'pointer',
                                    }}>
                                    {getProximityInfo(row?.proximity).text}
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: '140px', minWidth: '140px' }}>
                                <MenuList>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleProximityChange(row, '0-1')}
                                        style={{ backgroundColor: getProximityInfo('0-1').bg, color: 'white' }}>
                                        {getProximityInfo('0-1').text}
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleProximityChange(row, '1-3')}
                                        style={{ backgroundColor: getProximityInfo('1-3').bg, color: 'white' }}>
                                        {getProximityInfo('1-3').text}
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleProximityChange(row, '3-12')}
                                        style={{ backgroundColor: getProximityInfo('3-12').bg, color: 'white' }}>
                                        {getProximityInfo('3-12').text}
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleProximityChange(row, '12+')}
                                        style={{ backgroundColor: getProximityInfo('12+').bg, color: 'white' }}>
                                        {getProximityInfo('12+').text}
                                    </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </td>
                );
            case 'owner':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.owner }}>
                        <Menu open={ownerMenuOpenRowId === row.id} onOpenChange={(e, data) => setOwnerMenuOpenRowId(data.open ? row.id : null)}>
                            <MenuTrigger>
                                <div
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <CustomAvatar name={row.owner || ''} size={20} color={row.owner ? 'var(--Brand-Primary)' : '#6c757d'} />
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: '232px', maxHeight: '400px', overflowY: 'auto' }}>
                                <MenuList>
                                    {/* Owners list */}
                                    {allOwners.length > 0 ? (
                                        allOwners.map(ownerName => (
                                            <div key={ownerName} onClick={() => handleOwnerSelect(row, ownerName)} className={styles.tagMenuItem}>
                                                <span>{ownerName}</span>
                                            </div>
                                        ))
                                    ) : (
                                        <div>
                                            <p>No owners available</p>
                                        </div>
                                    )}

                                    {/* Add new owner - sticky bottom */}
                                    <div
                                        style={{
                                            position: 'sticky',
                                            bottom: -5,
                                            backgroundColor: 'white',
                                            padding: '8px 12px',
                                            marginTop: '8px',
                                        }}>
                                        {/* <MenuDivider /> */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="text"
                                                placeholder="Add new owner"
                                                className={styles.tagInput}
                                                value={newOwnerName}
                                                style={{ width: '170px' }}
                                                onChange={e => setNewOwnerName(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleAddOwner();
                                                    }
                                                }}
                                            />
                                            <div>
                                                <AddCircle24Filled
                                                    color="var(--Brand-Secondary)"
                                                    onClick={handleAddOwner}
                                                    disabled={!newOwnerName.trim()}
                                                    style={{ cursor: 'pointer', fontSize: '20px' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </td>
                );
            case 'strategy':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.strategy }}>
                        {editingStrategyId === row.id ? (
                            <input
                                type="text"
                                className={'form-control form-control-sm ' + styles.taskTextField}
                                value={editingStrategyText}
                                onChange={e => {
                                    e.stopPropagation();
                                    setEditingStrategyText(e.target.value);
                                }}
                                onKeyDown={e => handleStrategyKeyPress(e, row)}
                                onBlur={e => {
                                    e.stopPropagation();
                                    handleStrategySave(row);
                                }}
                                autoFocus
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <div
                                className={styles.taskText}
                                title={row.strategy || 'Pending Input'}
                                onClick={e => handleStrategyClick(e, row)}
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    width: '100%',
                                    color: row.strategy ? 'var(--Brand-Primary)' : 'var(--Neutral-Gray-6)',
                                }}>
                                {row.strategy || 'Pending Input'}
                            </div>
                        )}
                    </td>
                );
            case 'rag':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.rag }}>
                        <Menu>
                            <MenuTrigger>
                                <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                    <div
                                        className={styles.ragDiv}
                                        style={{
                                            backgroundColor: getRagInfo(row?.rag).bg,
                                            cursor: 'pointer',
                                        }}
                                    />
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: '80px', minWidth: '80px' }}>
                                <MenuList>
                                    <MenuItem className={styles.statusMenuItem} onClick={() => handleRagChange(row, 'RED')} style={{ backgroundColor: 'transparent' }}>
                                        <div
                                            className={styles.ragDiv}
                                            style={{
                                                backgroundColor: getRagInfo('RED').bg,
                                                margin: '0 auto',
                                            }}
                                        />
                                    </MenuItem>
                                    <MenuItem className={styles.statusMenuItem} onClick={() => handleRagChange(row, 'AMBER')} style={{ backgroundColor: 'transparent' }}>
                                        <div
                                            className={styles.ragDiv}
                                            style={{
                                                backgroundColor: getRagInfo('AMBER').bg,
                                                margin: '0 auto',
                                            }}
                                        />
                                    </MenuItem>
                                    <MenuItem className={styles.statusMenuItem} onClick={() => handleRagChange(row, 'GREEN')} style={{ backgroundColor: 'transparent' }}>
                                        <div
                                            className={styles.ragDiv}
                                            style={{
                                                backgroundColor: getRagInfo('GREEN').bg,
                                                margin: '0 auto',
                                            }}
                                        />
                                    </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </td>
                );
            case 'loggedOn':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.loggedOn }}>
                        <div className="d-flex align-items-center gap-2 px-2 justify-content-center">
                            <div>
                                <CustomAvatar name={row.created_by_name || ''} size={20} />
                            </div>
                            <span style={{ width: 60 }} className={styles.tableRegularText}>
                                {formatDate(row.created)}
                            </span>
                        </div>
                    </td>
                );
            case 'updatedOn':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.updatedOn }}>
                        <div className="d-flex align-items-center gap-2 justify-content-start px-2">
                            <div>
                                <CustomAvatar name={row.last_updated_by_name || ''} size={20} />
                            </div>
                            <span className={styles.tableRegularText}>{formatLastUpdated(row?.last_updated)}</span>
                        </div>
                    </td>
                );
            case 'status':
                return (
                    <td key={col} className={styles.tableRowItem} style={{ width: columnWidths.status }}>
                        <Menu>
                            <MenuTrigger>
                                <div
                                    className={styles.statusDiv}
                                    style={{
                                        backgroundColor: getStatusInfo(row?.status).bg,
                                        color: 'white',
                                        cursor: 'pointer',
                                    }}>
                                    {getStatusInfo(row?.status).text}
                                </div>
                            </MenuTrigger>
                            <MenuPopover style={{ width: '160px', minWidth: '160px' }}>
                                <MenuList>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleStatusChange(row, 'COMPLETED')}
                                        style={{ backgroundColor: getStatusInfo('COMPLETED').bg, color: 'white' }}>
                                        Completed
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleStatusChange(row, 'WORKING_ON_IT')}
                                        style={{ backgroundColor: getStatusInfo('WORKING_ON_IT').bg, color: 'white' }}>
                                        Working on it
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleStatusChange(row, 'STUCK')}
                                        style={{ backgroundColor: getStatusInfo('STUCK').bg, color: 'white' }}>
                                        Stuck
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleStatusChange(row, 'NOT_STARTED')}
                                        style={{ backgroundColor: getStatusInfo('NOT_STARTED').bg, color: 'white' }}>
                                        Not Started
                                    </MenuItem>
                                    <MenuItem
                                        className={styles.statusMenuItem}
                                        onClick={() => handleStatusChange(row, 'CANCELLED')}
                                        style={{ backgroundColor: getStatusInfo('CANCELLED').bg, color: 'white' }}>
                                        Cancelled
                                    </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </td>
                );
            default:
                return null;
        }
    };

    return (
        <div className={styles.viewHeight}>
            {/* Header with controls (copied style from TaskManagement) */}
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-4">
                    <h4 className={styles.pageTitle + ' mb-0'}>RAID Log</h4>
                </div>
                <div className="d-flex gap-4">
                    {selectedRows.length > 0 ? (
                        <div style={{ marginBottom: 8 }} className="d-flex align-items-center gap-2">
                            {/* Lens AI Analysis */}
                            <button
                                type="button"
                                className={`${styles.bulkUpdateMenu} d-flex align-items-center`}
                                onClick={() => {
                                    setSelectedSummaryTab('overall-risk');
                                    setShowLensAIAnalysis(true);
                                }}>
                                <Wand16Regular className="me-1" /> Lens AI Analysis
                            </button>
                            {/* Category bulk update */}
                            <Menu>
                                <MenuTrigger>
                                    <div className={styles.bulkUpdateMenu}>
                                        Category <ChevronDown12Regular className="ms-1" />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { raid_category: 'RISK' },
                                                    }),
                                                )
                                            }>
                                            Risk
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { raid_category: 'ASSUMPTION' },
                                                    }),
                                                )
                                            }>
                                            Assumption
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { raid_category: 'ISSUE' },
                                                    }),
                                                )
                                            }>
                                            Issue
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { raid_category: 'DEPENDENCY' },
                                                    }),
                                                )
                                            }>
                                            Dependency
                                        </MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            {/* Level bulk update */}
                            <Menu>
                                <MenuTrigger>
                                    <div className={styles.bulkUpdateMenu}>
                                        Level <ChevronDown12Regular className="ms-1" />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { level: 'NEGLIGIBLE' },
                                                    }),
                                                )
                                            }>
                                            Negligible
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { level: 'LOW' },
                                                    }),
                                                )
                                            }>
                                            Low
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { level: 'MODERATE' },
                                                    }),
                                                )
                                            }>
                                            Moderate
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { level: 'HIGH' },
                                                    }),
                                                )
                                            }>
                                            High
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { level: 'CRITICAL' },
                                                    }),
                                                )
                                            }>
                                            Critical
                                        </MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            {/* Probability bulk update */}
                            <Menu>
                                <MenuTrigger>
                                    <div className={styles.bulkUpdateMenu}>
                                        Probability <ChevronDown12Regular className="ms-1" />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { probability: 'HIGH' },
                                                    }),
                                                )
                                            }>
                                            High
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { probability: 'MEDIUM' },
                                                    }),
                                                )
                                            }>
                                            Medium
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { probability: 'LOW' },
                                                    }),
                                                )
                                            }>
                                            Low
                                        </MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            {/* Proximity bulk update */}
                            <Menu>
                                <MenuTrigger>
                                    <div className={styles.bulkUpdateMenu}>
                                        Proximity <ChevronDown12Regular className="ms-1" />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { proximity: '0-1' },
                                                    }),
                                                )
                                            }>
                                            0 - 1 mo
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { proximity: '1-3' },
                                                    }),
                                                )
                                            }>
                                            1 - 3 mo
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { proximity: '3-12' },
                                                    }),
                                                )
                                            }>
                                            3 - 12 mo
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { proximity: '12+' },
                                                    }),
                                                )
                                            }>
                                            12+ mo
                                        </MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            {/* RAG bulk update */}
                            <Menu>
                                <MenuTrigger>
                                    <div className={styles.bulkUpdateMenu}>
                                        RAG <ChevronDown12Regular className="ms-1" />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { rag: 'RED' },
                                                    }),
                                                )
                                            }>
                                            Red
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { rag: 'AMBER' },
                                                    }),
                                                )
                                            }>
                                            Amber
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { rag: 'GREEN' },
                                                    }),
                                                )
                                            }>
                                            Green
                                        </MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            {/* Status bulk update */}
                            <Menu>
                                <MenuTrigger>
                                    <div className={styles.bulkUpdateMenu}>
                                        Status <ChevronDown12Regular className="ms-1" />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { status: 'NOT_STARTED' },
                                                    }),
                                                )
                                            }>
                                            Not Started
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { status: 'WORKING_ON_IT' },
                                                    }),
                                                )
                                            }>
                                            Working on it
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { status: 'COMPLETED' },
                                                    }),
                                                )
                                            }>
                                            Completed
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { status: 'STUCK' },
                                                    }),
                                                )
                                            }>
                                            Stuck
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() =>
                                                dispatch(
                                                    BulkUpdateRaidItems({
                                                        raidItemIds: selectedRows,
                                                        updates: { status: 'CANCELLED' },
                                                    }),
                                                )
                                            }>
                                            Cancelled
                                        </MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <Menu>
                                <MenuTrigger disableButtonEnhancement>
                                    <span
                                        className={styles.linkText}
                                        style={{
                                            cursor: exportCSVLoading || exportPDFLoading ? 'not-allowed' : 'pointer',
                                            opacity: exportCSVLoading || exportPDFLoading ? 0.6 : 1,
                                        }}>
                                        Export ({selectedRows.length})
                                    </span>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem onClick={handleExportSelectedRaidCSV} disabled={exportCSVLoading}>
                                            Export as CSV
                                        </MenuItem>
                                        <MenuItem onClick={() => handleExportRaidPDF(true)} disabled={exportPDFLoading}>
                                            Export as PDF
                                        </MenuItem>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <span
                                role="button"
                                className={styles.linkText}
                                onClick={() => {
                                    deleteModalRef?.current?.click();
                                }}>
                                {' '}
                                Delete ({selectedRows.length})
                            </span>
                        </div>
                    ) : (
                        <>
                            <ToolbarSearchMenu query={searchQuery} onQueryChange={setSearchQuery} placeholder="Search RAID…" ariaLabel="Search RAID" />
                            <Menu
                                open={filterMenuOpen}
                                onOpenChange={(event, data) => {
                                    if (!data.open && isFilterSelectMenuTarget(event?.target)) return;
                                    setFilterMenuOpen(data.open);
                                }}>
                                <MenuTrigger>
                                    <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                        <Filter20Regular className="me-1" />
                                        Filter ({filters.filter(filterHasSelection).length})
                                    </span>
                                </MenuTrigger>
                                <MenuPopover style={{ width: '320px', minWidth: '320px' }}>
                                    <MenuList>
                                        <div className="p-2">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <h6 className={styles.tableFilterHeader + ' mb-0'}>Filter Items By</h6>
                                                {filters.some(filterHasSelection) && (
                                                    <span className={styles.secondaryBtnText} onClick={handleClearAllFilters}>
                                                        Clear All
                                                    </span>
                                                )}
                                            </div>

                                            {filters.map(filter => (
                                                <div key={filter.id} className={styles.filterRow}>
                                                    <div className={styles.filterRowControls}>
                                                        <select
                                                            className={'form-select form-select-sm ' + styles.filterSelect + ' ' + styles.filterColumnSelect}
                                                            value={filter.column}
                                                            onChange={e => handleUpdateFilter(filter.id, 'column', e.target.value)}>
                                                            <option value="">Select Column</option>
                                                            {getFilterColumnOptions().map(option => (
                                                                <option key={option.value} value={option.value}>
                                                                    {option.label}
                                                                </option>
                                                            ))}
                                                        </select>

                                                        {filters.length > 1 && (
                                                            <button className="btn btn-link p-0" onClick={() => handleRemoveFilter(filter.id)} style={{ color: '#6c757d' }}>
                                                                ✕
                                                            </button>
                                                        )}
                                                    </div>
                                                    <FilterMultiSelect
                                                        options={getFilterValueOptions(filter.column)}
                                                        value={filter.value}
                                                        disabled={!filter.column}
                                                        onChange={value => handleUpdateFilter(filter.id, 'value', value)}
                                                    />
                                                </div>
                                            ))}

                                            <span className={styles.secondaryBtnText + ' ms-1'} onClick={handleAddFilter}>
                                                Add another filter
                                            </span>
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <Menu>
                                <MenuTrigger>
                                    <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                        <ArrowSort20Regular className="me-1" />
                                        Sort
                                    </span>
                                </MenuTrigger>
                                <MenuPopover style={{ width: '280px', minWidth: '280px' }}>
                                    <MenuList>
                                        <div className="p-2">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <h6 className={styles.tableFilterHeader + ' mb-0'}>Sort Items By</h6>
                                                {(sortColumn || sortOrder) && (
                                                    <span className={styles.secondaryBtnText} onClick={handleClearSort}>
                                                        Clear
                                                    </span>
                                                )}
                                            </div>

                                            <div className="d-flex align-items-center gap-2">
                                                <select
                                                    className={'form-select form-select-sm ' + styles.filterSelect}
                                                    value={sortColumn}
                                                    onChange={e => setSortColumn(e.target.value)}>
                                                    <option value="">Select column</option>
                                                    {getSortColumnOptions().map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>

                                                <select
                                                    className={'form-select form-select-sm ' + styles.filterSelect}
                                                    value={sortOrder}
                                                    onChange={e => setSortOrder(e.target.value)}
                                                    disabled={!sortColumn}>
                                                    <option value="">Select sorting</option>
                                                    {getSortOrderOptions().map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <Menu>
                                <MenuTrigger>
                                    <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                        <Grid20Regular className="me-1" />
                                        Group by
                                    </span>
                                </MenuTrigger>
                                <MenuPopover style={{ width: '280px', minWidth: '280px' }}>
                                    <MenuList>
                                        <div className="p-2">
                                            <div className="d-flex align-items-center justify-content-between mb-3">
                                                <h6 className={styles.tableFilterHeader + ' mb-0'}>Group Items By</h6>
                                                {(groupByColumn || groupBySort) && (
                                                    <span
                                                        className={styles.secondaryBtnText}
                                                        onClick={() => {
                                                            setGroupByColumn('');
                                                            setGroupBySort('');
                                                        }}>
                                                        Clear
                                                    </span>
                                                )}
                                            </div>

                                            <div className="d-flex align-items-center gap-2">
                                                <select
                                                    className={'form-select form-select-sm ' + styles.filterSelect}
                                                    value={groupByColumn}
                                                    onChange={e => setGroupByColumn(e.target.value)}>
                                                    <option value="">Select column</option>
                                                    <option value="raid_category">RAID Category</option>
                                                    <option value="level">Level</option>
                                                    <option value="probability">Probability</option>
                                                    <option value="proximity">Proximity</option>
                                                    <option value="owner">Owner</option>
                                                    <option value="rag">RAG</option>
                                                    <option value="status">Status</option>
                                                </select>

                                                <select
                                                    className={'form-select form-select-sm ' + styles.filterSelect}
                                                    value={groupBySort}
                                                    onChange={e => setGroupBySort(e.target.value)}
                                                    disabled={!groupByColumn}>
                                                    <option value="">Select Sorting</option>
                                                    <option value="asc">Ascending</option>
                                                    <option value="desc">Descending</option>
                                                </select>
                                            </div>
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <Menu>
                                <MenuTrigger>
                                    <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                                        <EyeOff20Regular className="me-1" />
                                        Hide
                                    </span>
                                </MenuTrigger>
                                <MenuPopover style={{ width: '200px', minWidth: '200px' }}>
                                    <MenuList>
                                        <div className="p-2">
                                            <h6 className={styles.tableFilterHeader + ' mb-3'}>Display Columns</h6>

                                            {getColumnVisibilityOptions().map(option => (
                                                <div key={option.value} className="d-flex align-items-center mb-2">
                                                    <input
                                                        type="checkbox"
                                                        style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            marginRight: '8px',
                                                            cursor: 'pointer',
                                                        }}
                                                        checked={option.value === 'showAll' ? showAllColumns : isColumnVisible(option.value)}
                                                        onChange={e => handleColumnVisibility(option.value, e.target.checked)}
                                                    />
                                                    <span className={styles.tableRegularText}>{option.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                            <div className="d-flex align-items-center">
                                <button className={styles.regularSecondaryBtn} onClick={handleAddNewRaid}>
                                    Add RAID Item
                                </button>
                                <Menu>
                                    <MenuTrigger>
                                        <button className={styles.menuButton}>
                                            <ChevronDown12Regular />
                                        </button>
                                    </MenuTrigger>
                                    <MenuPopover>
                                        <MenuList>
                                            <MenuItem icon={<ArrowExport20Regular />} style={{ color: 'var(--Brand-Secondary)' }} onClick={handleExportRaidCSV}>
                                                Export RAID as CSV
                                            </MenuItem>
                                            <MenuItem
                                                icon={<ArrowExport20Regular />}
                                                style={{ color: 'var(--Brand-Secondary)' }}
                                                disabled={exportPDFLoading}
                                                onClick={() => handleExportRaidPDF(false)}>
                                                Export RAID as PDF
                                            </MenuItem>
                                            <MenuDivider />
                                            <MenuItem icon={<Table20Regular />} style={{ color: 'var(--Brand-Secondary)' }} onClick={handleImportRaidCSV}>
                                                Import RAID from CSV
                                            </MenuItem>
                                            <MenuDivider />
                                            <MenuItem icon={<ArrowDownload20Regular />} style={{ color: 'var(--Brand-Secondary)' }} onClick={handleDownloadRaidTemplate}>
                                                Download RAID Template
                                            </MenuItem>
                                        </MenuList>
                                    </MenuPopover>
                                </Menu>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Table with draggable + resizable columns (same mechanics as TasksTable) */}
            {/* Hidden file input for RAID CSV upload */}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleRaidFileUpload} className="d-none" id="csv-upload-raid" disabled={uploadCSVBtnLoading} />

            <div className={styles.tableContainer} ref={tableContainerRef} id="raid-table-scroll">
                <table style={{ minWidth: totalTableWidth }}>
                    <colgroup>
                        {/* ID */}
                        <col style={{ width: columnWidths.id }} />
                        {/* Main dynamic columns in current order */}
                        {columnOrder.map(col => (
                            <col key={col} style={{ width: columnWidths[col] }} />
                        ))}
                        {/* Actions */}
                        <col style={{ width: columnWidths.actions }} />
                    </colgroup>
                    {!groupByColumn && (
                        <thead>
                            <DndContext
                                collisionDetection={closestCenter}
                                onDragStart={() => {
                                    document.body.style.cursor = 's-resize';
                                }}
                                onDragEnd={event => {
                                    const { active, over } = event;
                                    if (!over || active.id === over.id) return;
                                    const oldIndex = columnOrder.indexOf(String(active.id));
                                    const newIndex = columnOrder.indexOf(String(over.id));
                                    if (oldIndex === -1 || newIndex === -1) return;
                                    const updated = [...columnOrder];
                                    const [moved] = updated.splice(oldIndex, 1);
                                    updated.splice(newIndex, 0, moved);
                                    setColumnOrder(updated);
                                    document.body.style.cursor = '';
                                }}
                                onDragCancel={() => {
                                    document.body.style.cursor = '';
                                }}>
                                <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                                    <tr className={styles.tableHeader}>
                                        {/* Static ID column with select-all checkbox on hover */}
                                        <th
                                            className={styles.headerRowItem}
                                            style={{
                                                width: columnWidths.id,
                                                minWidth: columnWidths.id,
                                                maxWidth: columnWidths.id,
                                            }}
                                            onMouseEnter={() => setHeaderIdHovered(true)}
                                            onMouseLeave={() => setHeaderIdHovered(false)}>
                                            {headerIdHovered || allVisibleSelected ? (
                                                <input type="checkbox" className={styles.checkboxTable} checked={allVisibleSelected} onChange={handleToggleSelectAll} />
                                            ) : (
                                                <span className={styles.headerRowItem}>ID</span>
                                            )}
                                        </th>

                                        {/* Draggable + resizable RAID columns */}
                                        {columnOrder.map(col => {
                                            if (!isColumnVisible(col)) return null;

                                            if (col === 'raidItem') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.raidItem}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths.raidItem}
                                                            minWidth={200}
                                                            maxWidth={800}
                                                            onResize={newWidth => handleColumnResize('raidItem', newWidth)}>
                                                            RAID Item
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            if (col === 'impact') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.impact}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths.impact}
                                                            minWidth={200}
                                                            maxWidth={800}
                                                            onResize={newWidth => handleColumnResize('impact', newWidth)}>
                                                            Impact
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            if (col === 'strategy') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.strategy}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths.strategy}
                                                            minWidth={200}
                                                            maxWidth={800}
                                                            onResize={newWidth => handleColumnResize('strategy', newWidth)}>
                                                            Strategy / Mitigation
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }

                                            const labelMap = {
                                                category: 'RAID Category',
                                                level: 'Level',
                                                probability: 'Probability',
                                                proximity: 'Proximity',
                                                owner: 'Owner',
                                                rag: 'RAG',
                                                loggedOn: 'Logged on',
                                                updatedOn: 'Updated on',
                                                status: 'Current Status',
                                            };

                                            return (
                                                <SortableHeaderCell key={col} id={col} width={columnWidths[col]}>
                                                    {labelMap[col]}
                                                </SortableHeaderCell>
                                            );
                                        })}

                                        {/* Static actions column */}
                                        <th
                                            className={styles.headerRowItem}
                                            style={{
                                                width: columnWidths.actions,
                                                minWidth: columnWidths.actions,
                                                maxWidth: columnWidths.actions,
                                            }}
                                        />
                                    </tr>
                                </SortableContext>
                            </DndContext>
                        </thead>
                    )}
                    <tbody>
                        {(() => {
                            const flatItems = flatVisibleItems;
                            const grouped = applyGrouping(flatItems);

                            // Helper to format a 1-based index as a 4-digit string, e.g. 1 -> "0001"
                            const formatDisplayId = idx => String(idx + 1).padStart(4, '0');

                            if (!grouped) {
                                const renderIdCell = (row, index) => {
                                    const showCheckbox = isRowSelected(row.id) || hoveredRowId === row.id;
                                    if (showCheckbox) {
                                        return (
                                            <input
                                                type="checkbox"
                                                className={styles.checkboxTable}
                                                checked={isRowSelected(row.id)}
                                                onChange={e => {
                                                    e.stopPropagation();
                                                    handleSelectRow(row.id, e.nativeEvent || e);
                                                }}
                                            />
                                        );
                                    }
                                    return formatDisplayId(index);
                                };

                                return flatItems.map((row, index) => (
                                    <tr
                                        key={row.id || index}
                                        className={styles.tableRow}
                                        style={{ backgroundColor: isRowSelected(row.id) ? '#F2F6FE' : undefined }}
                                        onPointerDown={e => handleRowPointerDown(row.id, e)}
                                        onMouseEnter={e => {
                                            setHoveredRowId(row.id);
                                            const menuIcon = e.currentTarget.querySelector('.menu-hover-icon');
                                            if (menuIcon) {
                                                menuIcon.style.display = 'flex';
                                            }
                                        }}
                                        onMouseLeave={e => {
                                            setHoveredRowId(null);
                                            const menuIcon = e.currentTarget.querySelector('.menu-hover-icon');
                                            if (menuIcon) {
                                                menuIcon.style.display = 'none';
                                            }
                                        }}>
                                        {/* Static ID cell */}
                                        <td className={styles.tableRowItem} style={{ width: columnWidths.id }}>
                                            {renderIdCell(row, index)}
                                        </td>

                                        {/* Dynamic cells following the current column order */}
                                        {columnOrder.filter(isColumnVisible).map(col => renderBodyCell(row, col))}

                                        {/* Static actions cell */}
                                        <td className={styles.tableRowItem} style={{ width: columnWidths.actions }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                <div className="menu-hover-icon" style={{ display: 'none' }}>
                                                    <Menu>
                                                        <MenuTrigger>
                                                            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <MoreVertical16Regular style={{ width: '16px', height: '16px' }} />
                                                            </div>
                                                        </MenuTrigger>
                                                        <MenuPopover>
                                                            <MenuList>
                                                                <MenuItem onClick={() => handleDeleteRow(row)}>Delete</MenuItem>
                                                            </MenuList>
                                                        </MenuPopover>
                                                    </Menu>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ));
                            }

                            const visibleColumnsCount = 1 + columnOrder.filter(isColumnVisible).length + 1; // ID + visible + actions

                            const renderGroupedIdCell = (row, index) => {
                                const showCheckbox = isRowSelected(row.id) || hoveredRowId === row.id;
                                if (showCheckbox) {
                                    return (
                                        <input
                                            type="checkbox"
                                            className={styles.checkboxTable}
                                            checked={isRowSelected(row.id)}
                                            onChange={e => {
                                                e.stopPropagation();
                                                handleSelectRow(row.id, e.nativeEvent || e);
                                            }}
                                        />
                                    );
                                }
                                return formatDisplayId(index);
                            };

                            return grouped.map(group => (
                                <React.Fragment key={group.groupName}>
                                    <tr>
                                        <td colSpan={visibleColumnsCount} style={{ padding: '8px 12px' }}>
                                            <span
                                                className={styles.todoText}
                                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                                                onClick={() =>
                                                    setCollapsedGroups(prev => {
                                                        const next = new Set(prev);
                                                        if (next.has(group.groupName)) next.delete(group.groupName);
                                                        else next.add(group.groupName);
                                                        return next;
                                                    })
                                                }>
                                                <ChevronDown20Regular className="me-1" style={collapsedGroups.has(group.groupName) ? { transform: 'rotate(-90deg)' } : {}} />
                                                {group.groupName} ({group.items.length})
                                            </span>
                                        </td>
                                    </tr>
                                    {!collapsedGroups.has(group.groupName) && (
                                        <>
                                            {/* Per-group header row */}
                                            <tr className={styles.tableHeader}>
                                                <th
                                                    className={styles.headerRowItem}
                                                    style={{
                                                        width: columnWidths.id,
                                                        minWidth: columnWidths.id,
                                                        maxWidth: columnWidths.id,
                                                    }}>
                                                    ID
                                                </th>
                                                {columnOrder.map(col => {
                                                    if (!isColumnVisible(col)) return null;
                                                    const labelMap = {
                                                        raidItem: 'RAID Item',
                                                        category: 'RAID Category',
                                                        impact: 'Impact',
                                                        level: 'Level',
                                                        probability: 'Probability',
                                                        proximity: 'Proximity',
                                                        owner: 'Owner',
                                                        strategy: 'Strategy / Mitigation',
                                                        rag: 'RAG',
                                                        loggedOn: 'Logged on',
                                                        updatedOn: 'Updated on',
                                                        status: 'Status',
                                                    };
                                                    return (
                                                        <th key={col} className={styles.headerRowItem} style={{ width: columnWidths[col] }}>
                                                            {labelMap[col]}
                                                        </th>
                                                    );
                                                })}
                                                <th
                                                    className={styles.headerRowItem}
                                                    style={{
                                                        width: columnWidths.actions,
                                                        minWidth: columnWidths.actions,
                                                        maxWidth: columnWidths.actions,
                                                    }}
                                                />
                                            </tr>

                                            {group.items.map((row, index) => (
                                                <tr
                                                    key={row.id || index}
                                                    className={styles.tableRow}
                                                    style={{ backgroundColor: isRowSelected(row.id) ? '#F2F6FE' : undefined }}
                                                    onPointerDown={e => handleRowPointerDown(row.id, e)}
                                                    onMouseEnter={e => {
                                                        setHoveredRowId(row.id);
                                                        const menuIcon = e.currentTarget.querySelector('.menu-hover-icon');
                                                        if (menuIcon) {
                                                            menuIcon.style.display = 'flex';
                                                        }
                                                    }}
                                                    onMouseLeave={e => {
                                                        setHoveredRowId(null);
                                                        const menuIcon = e.currentTarget.querySelector('.menu-hover-icon');
                                                        if (menuIcon) {
                                                            menuIcon.style.display = 'none';
                                                        }
                                                    }}>
                                                    {/* Static ID cell */}
                                                    <td className={styles.tableRowItem} style={{ width: columnWidths.id }}>
                                                        {renderGroupedIdCell(row, index)}
                                                    </td>

                                                    {/* Dynamic cells following the current column order */}
                                                    {columnOrder.filter(isColumnVisible).map(col => renderBodyCell(row, col))}

                                                    {/* Static actions cell */}
                                                    <td className={styles.tableRowItem} style={{ width: columnWidths.actions }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                                            <div className="menu-hover-icon" style={{ display: 'none' }}>
                                                                <Menu>
                                                                    <MenuTrigger>
                                                                        <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                            <MoreVertical16Regular style={{ width: '16px', height: '16px' }} />
                                                                        </div>
                                                                    </MenuTrigger>
                                                                    <MenuPopover>
                                                                        <MenuList>
                                                                            <MenuItem onClick={() => handleDeleteRow(row)}>Delete</MenuItem>
                                                                        </MenuList>
                                                                    </MenuPopover>
                                                                </Menu>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </>
                                    )}
                                </React.Fragment>
                            ));
                        })()}

                        {isAddingNewRaid &&
                            (() => {
                                // Calculate total number of columns: ID + visible columns + actions
                                const visibleColumnsCount = columnOrder.filter(col => isColumnVisible(col)).length;
                                const totalColumns = 1 + visibleColumnsCount + 1; // ID + visible columns + actions

                                return (
                                    <tr
                                        ref={addRowRef}
                                        className={`${styles.tableRow} ${highlightAddRow ? styles.highlightAddRow : ''}`}
                                        style={{ backgroundColor: '#F3FDFE', position: 'sticky', bottom: 0, zIndex: 1 }}>
                                        {/* Single cell with colspan spanning all columns */}
                                        <td className={styles.tableRowItem} colSpan={totalColumns}>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <div className="flex-fill">
                                                    <input
                                                        ref={newRaidInputRef}
                                                        type="text"
                                                        className={'form-control form-control-sm ' + styles.taskTextField}
                                                        value={newRaidText}
                                                        style={{ border: '1px solid #dddee4', marginLeft: '60px', maxWidth: '400px' }}
                                                        onChange={e => setNewRaidText(e.target.value)}
                                                        onKeyDown={async e => {
                                                            if (e.key === 'Enter') {
                                                                await handleCreateRaid();
                                                            } else if (e.key === 'Escape') {
                                                                // Just clear text, keep row visible (same behaviour as TaskManagement)
                                                                setNewRaidText('');
                                                            }
                                                        }}
                                                        onBlur={async () => {
                                                            const value = newRaidText.trim();
                                                            if (value) {
                                                                await handleCreateRaid();
                                                            } else {
                                                                // Clear text but do not hide the add row
                                                                setNewRaidText('');
                                                            }
                                                        }}
                                                        placeholder="Add new RAID item"
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })()}
                    </tbody>
                </table>
            </div>
            {/* Delete Confirmation Modal for RAID items (single + bulk delete) */}
            <DoubleConfirmationModal
                message="Deleting RAID item(s) will permanently remove them from the Project."
                handleAction={() => {
                    if (selectedRows.length > 0) {
                        dispatch(BulkDeleteRaidItems({ raidItemIds: selectedRows }));
                        setSelectedRows([]);
                    } else if (selectedRowForDelete) {
                        dispatch(DeleteRaidItem(selectedRowForDelete.id));
                        setSelectedRowForDelete(null);
                    }
                }}>
                <span ref={deleteModalRef} style={{ display: 'none' }}></span>
            </DoubleConfirmationModal>
            <RaidDetailsSidebar open={raidSidebarOpen} onClose={handleCloseRaidSidebar} raidItem={selectedRaidForSidebar} />

            {/* Lens AI RAID Analysis Sidebar */}
            <OverlayDrawer
                style={{ minWidth: 872 }}
                type="overlay"
                position="end"
                open={showLensAIAnalysis}
                onOpenChange={(_, { open }) => {
                    setShowLensAIAnalysis(open);
                    if (!open) setSelectedSummaryTab('overall-risk');
                }}>
                <DrawerBody className="position-relative" style={{ padding: 0 }}>
                    <div className="d-flex align-items-center justify-content-between px-3 mt-3">
                        <div className="d-flex align-items-center">
                            <Wand20Regular color="var(--Brand-Secondary)" className="me-1" />
                            <span style={{ fontWeight: 600, fontSize: 16 }}>Lens AI Analysis</span>
                        </div>
                        <Dismiss20Regular onClick={() => setShowLensAIAnalysis(false)} role="button" color="var(--Neutral-Gray-6)" style={{ cursor: 'pointer' }} />
                    </div>

                    <div className="d-flex flex-wrap align-items-center gap-2 px-3 mt-3" style={{ borderBottom: '1px solid var(--Neutral-Gray-4)', paddingBottom: 12 }}>
                        <div className="d-flex align-items-center gap-2 flex-grow-1">
                            <label htmlFor="raid-topic-select" className="mb-0" style={{ fontSize: 12, color: 'var(--Brand-Primary)', whiteSpace: 'nowrap' }}>
                                Select topic to view:
                            </label>
                            <select
                                id="raid-topic-select"
                                value={selectedSummaryTab}
                                onChange={e => setSelectedSummaryTab(e.target.value)}
                                className="form-select form-select-sm"
                                style={{ maxWidth: 320, fontSize: 12 }}>
                                {RAID_AI_SUMMARY_SECTIONS.map(({ id, label }) => (
                                    <option key={id} value={id}>
                                        {label}
                                    </option>
                                ))}
                                <option value={RAID_AI_SHOW_ALL}>Show all</option>
                            </select>
                        </div>
                        <div
                            onClick={() => {
                                if (!raidSummaryLoading) {
                                    dispatch(GetRaidAISummary({ raidItemIds: selectedRows, regenerate: true }));
                                }
                            }}
                            style={{
                                color: raidSummaryLoading ? 'var(--Neutral-Gray-6)' : 'var(--Brand-Secondary)',
                                cursor: raidSummaryLoading ? 'not-allowed' : 'pointer',
                                opacity: raidSummaryLoading ? 0.5 : 1,
                                marginLeft: 'auto',
                            }}
                            className="d-flex align-items-center"
                            role="button">
                            {raidSummaryLoading ? (
                                <Wand20Regular className="me-1" style={{ animation: 'pulse 1s ease-in-out infinite' }} />
                            ) : (
                                <ArrowClockwise16Regular className="me-1" />
                            )}
                            <span>{raidSummaryLoading ? 'Generating...' : 'Regenerate'}</span>
                        </div>
                    </div>

                    <div className="px-3 mt-4 position-relative" style={{ minHeight: 200 }}>
                        {raidSummaryLoading && (
                            <div className="d-flex flex-column align-items-center justify-content-center py-5">
                                <Wand20Regular style={{ color: 'var(--Brand-Secondary)' }} />
                                <p className="mt-2 mb-0" style={{ color: 'var(--Brand-Primary)', fontSize: 14 }}>
                                    Loading results
                                </p>
                            </div>
                        )}

                        {raidSummaryError && (
                            <div className="alert alert-danger" role="alert">
                                <strong>Error:</strong> {raidSummaryError}
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger ms-2"
                                    onClick={() => dispatch(GetRaidAISummary({ raidItemIds: selectedRows, regenerate: true }))}>
                                    Retry
                                </button>
                            </div>
                        )}

                        {!raidSummaryLoading && !raidSummaryError && raidSummaryData?.sections && (
                            <div style={{ color: 'var(--Brand-Primary)', fontSize: 12, lineHeight: 1.6 }}>
                                {selectedSummaryTab === RAID_AI_SHOW_ALL
                                    ? RAID_AI_SUMMARY_SECTIONS.map((section, index) => (
                                          <section
                                              key={section.id}
                                              style={{
                                                  marginBottom: index < RAID_AI_SUMMARY_SECTIONS.length - 1 ? 20 : 0,
                                                  paddingBottom: index < RAID_AI_SUMMARY_SECTIONS.length - 1 ? 20 : 0,
                                                  borderBottom:
                                                      index < RAID_AI_SUMMARY_SECTIONS.length - 1 ? '1px solid var(--Neutral-Gray-4)' : 'none',
                                              }}>
                                              <h3
                                                  className="mb-2 mt-0"
                                                  style={{
                                                      fontSize: 14,
                                                      fontWeight: 600,
                                                      color: 'var(--Brand-Secondary)',
                                                      letterSpacing: '0.01em',
                                                  }}>
                                                  {section.label}
                                              </h3>
                                              <div dangerouslySetInnerHTML={{ __html: raidSummaryData.sections[section.key] || '<p>No content.</p>' }} />
                                          </section>
                                      ))
                                    : RAID_AI_SUMMARY_SECTIONS.filter(t => t.id === selectedSummaryTab).map(t => (
                                          <div key={t.id} dangerouslySetInnerHTML={{ __html: raidSummaryData.sections[t.key] || '<p>No content.</p>' }} />
                                      ))}
                            </div>
                        )}

                        {!raidSummaryLoading && !raidSummaryError && !raidSummaryData?.sections && (
                            <div className="text-muted" style={{ fontSize: 14 }}>
                                Click &quot;Regenerate&quot; to analyze the selected RAID items.
                            </div>
                        )}
                    </div>
                </DrawerBody>
            </OverlayDrawer>
        </div>
    );
};

export default Raid;
